"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var service_cache_1 = require("./service-cache");
/**
 * The settings module provides the configured locations and authentication
 * details for the EVA back-end. All other EVA content and data classes rely on it.
 * This module is user internally in the client and exposes more methods then
 * the globally exposed settings namespace on the EVA Client
 *
 * NOTE: SettingsManager is the internal name. Externally it is simply exposed as 'settings'
 *
 * @preferred
 */
/* tslint:disable */
function uuid(a) {
    return a ? (0 | Math.random() * 16).toString(16) : ("" + 1e7 + -1e3 + -4e3 + -8e3 + -1e11).replace(/1|0/g, uuid);
}
/**
 * Internal settings for all EVA module classes
 * @type {IEvaSettings}
 */
var settings = {
    endPointURL: "https://eva.newblack.io",
    language: "nl-NL",
    version: "0.1.0",
    clientName: "eva-sdk-js",
    sessionId: uuid(),
    productProperties: undefined,
    appName: "UNKNOWN",
    appVersion: "0.0.0",
    applicationToken: "",
};
/**
 * The settings manager provides methods to get and set EVA client options
 * @type {namespace}
 */
var SettingsManager;
(function (SettingsManager) {
    /**
     * Generates a new session identifier for all future service calls
     * @return {string} The current session identifier
     */
    function generateSessionID() {
        // Flush the service cache when starting a new session
        //
        service_cache_1.serviceCache.flush();
        return settings.sessionId = uuid();
    }
    SettingsManager.generateSessionID = generateSessionID;
    /**
     * Changes the currently configured site authentication token.
     * @return {string} An authorisation token
     */
    function setSiteAuthenticationToken(token) {
        // Flush the service cache when changing token
        //
        service_cache_1.serviceCache.flush();
        return settings.siteAuthenticationToken = token;
    }
    SettingsManager.setSiteAuthenticationToken = setSiteAuthenticationToken;
    /**
     * Changes the currently configured user authentication token. This token
     * will be used in favor of the site authentication token.
     * @return {string} An authorisation token
     */
    function setUserAuthenticationToken(token) {
        // Flush the service cache when changing token
        //
        service_cache_1.serviceCache.flush();
        return settings.userAuthenticationToken = token;
    }
    SettingsManager.setUserAuthenticationToken = setUserAuthenticationToken;
    /**
     * Return the currently configured authentication token
     * @return {string} An authorisation token
     */
    function getAuthenticationToken() {
        return settings.userAuthenticationToken ? settings.userAuthenticationToken : settings.siteAuthenticationToken;
    }
    SettingsManager.getAuthenticationToken = getAuthenticationToken;
    /**
     * Changes the currently configured session identifier
     * @return {string} A session identifier
     */
    function setSessionId(sessionId) {
        // Flush the service cache when session
        //
        service_cache_1.serviceCache.flush();
        return settings.sessionId = sessionId;
    }
    SettingsManager.setSessionId = setSessionId;
    /**
     * Return the currently configured session identifier
     * @return {string} A session identifier
     */
    function getSessionId() {
        return settings.sessionId;
    }
    SettingsManager.getSessionId = getSessionId;
    /**
     * Return the currently configured end point URL
     * @return {string} An end point URL
     */
    function getEndPointURL() {
        return settings.endPointURL;
    }
    SettingsManager.getEndPointURL = getEndPointURL;
    /**
     * Changes the currently configured end point URL
     * @return {string} An end point URL
     */
    function setEndPointURL(URL) {
        // Flush the service cache when changing end point
        //
        service_cache_1.serviceCache.flush();
        return settings.endPointURL = URL;
    }
    SettingsManager.setEndPointURL = setEndPointURL;
    /**
     * Return the currently configured application identifier
     * @return {number} An application identifier
     */
    function getApplicationId() {
        return settings.applicationId;
    }
    SettingsManager.getApplicationId = getApplicationId;
    /**
     * Changes the currently configured application identifier
     * @return {number} An application identifier
     */
    function setApplicationId(applicationId) {
        return settings.applicationId = applicationId;
    }
    SettingsManager.setApplicationId = setApplicationId;
    /**
     * Returns the currently configured client language
     * @return {string} Language code (ex. nl-NL)
     */
    function getLanguage() {
        return settings.language;
    }
    SettingsManager.getLanguage = getLanguage;
    /**
     * Changes the currently configured client language
     * @return {string} Language code (ex. nl-NL)
     */
    function setLanguage(languageCode) {
        // Flush the service cache when changing language
        //
        service_cache_1.serviceCache.flush();
        return settings.language = languageCode;
    }
    SettingsManager.setLanguage = setLanguage;
    /**
     * Returns the current client version number which is in semver format
     * @return {string} Version number (ex. 0.1.0)
     */
    function getVersion() {
        return settings.version;
    }
    SettingsManager.getVersion = getVersion;
    /**
     * Returns the current client name
     * @return {string} Client name
     */
    function getClientName() {
        return settings.clientName;
    }
    SettingsManager.getClientName = getClientName;
    /**
     * Changes the currently configured client language
     * @return {string} Language code (ex. nl-NL)
     */
    function setAppDetails(name, version) {
        // Flush the service cache when changing app name
        //
        service_cache_1.serviceCache.flush();
        settings.appName = name;
        settings.appVersion = version;
        return settings.appName + "/" + settings.appVersion;
    }
    SettingsManager.setAppDetails = setAppDetails;
    /**
     * Returns the current app version number which is in semver format
     * @return {string} Version number (ex. 0.1.0)
     */
    function getAppVersion() {
        return settings.appVersion;
    }
    SettingsManager.getAppVersion = getAppVersion;
    /**
     * Returns the current app name
     * @return {string} App name
     */
    function getAppName() {
        return settings.appName;
    }
    SettingsManager.getAppName = getAppName;
    /**
     * Returns the currently configured product properties
     *
     * @export
     * @returns {string[]}
     */
    function getProductProperties() {
        return settings.productProperties;
    }
    SettingsManager.getProductProperties = getProductProperties;
    /**
     * Set the currently configured product properties
     *
     * @export
     * @param {string[]} properties
     * @returns {string[]}
     */
    function setProductProperties(properties) {
        return settings.productProperties = properties;
    }
    SettingsManager.setProductProperties = setProductProperties;
    /**
     * Changes the currently configured application token. This token is sent
     * to the EVA backend using the EVA-App-Token header
     *
     * @return {string} An application token
     */
    function setApplicationToken(token) {
        return settings.applicationToken = token;
    }
    SettingsManager.setApplicationToken = setApplicationToken;
    /**
     * Return the currently configured application token
     *
     * @return {string} An application token
     */
    function getApplicationToken() {
        return settings.applicationToken;
    }
    SettingsManager.getApplicationToken = getApplicationToken;
})(SettingsManager = exports.SettingsManager || (exports.SettingsManager = {}));
